import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Edit2, Users, Gift, Globe2, Camera, Sparkles, Crown, Heart, MessageCircle, LogOut, Star, Package, Lock, Eye, EyeOff, Zap, MessageSquare, Gamepad2, CheckCircle2, Bell, ShieldCheck, HelpCircle, RotateCcw, Settings as SettingsIcon, ChevronRight, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { COSMIC_RANKS } from '../mock/mock';
import api from '../lib/api';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import AvatarPicker from '../components/AvatarPicker';
import BannerPicker from '../components/BannerPicker';
import VipBadge from '../components/VipBadge';
import FounderBadge from '../components/FounderBadge';
import BuyStars from '../components/BuyStars';
import RankMedal, { ALL_RANK_MEDALS } from '../components/RankMedal';
import RelationshipBadge from '../components/RelationshipBadge';
import { isNativeMobile, restorePurchases } from '../lib/iap';
import { toast } from 'sonner';

export default function Profile() {
  const { user, updateProfile, logout, refresh } = useAuth();
  const [edit, setEdit] = useState({ displayName: user?.displayName || '', bio: user?.bio || '' });
  const [inventory, setInventory] = useState({ items: [], total_count: 0, total_value: 0, unique_types: 0 });
  const [history, setHistory] = useState([]);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    api.get(`/users/${user.user_id}/gifts-inventory`).then(r => setInventory(r.data)).catch(() => {});
    api.get(`/users/${user.user_id}/gifts-history`).then(r => setHistory(r.data)).catch(() => {});
  }, [user?.user_id, user?.giftsReceived]);

  if (!user) return null;
  const rank = COSMIC_RANKS.find(r => r.rank === user.rank) || COSMIC_RANKS[0];
  const next = COSMIC_RANKS.find(r => r.rank === user.rank + 1);
  const progress = next ? Math.min(100, (((user.xp || 0) - rank.xp) / Math.max(1, next.xp - rank.xp)) * 100) : 100;

  const save = async () => {
    const res = await updateProfile(edit);
    if (res.ok) toast.success('Profile updated');
    else toast.error(res.error || 'Could not save');
  };

  const saveAvatar = async (avatarUrl, meta = {}) => {
    const patch = { avatar: avatarUrl, avatar_is_ai: !!meta.isAiGenerated };
    const res = await updateProfile(patch);
    if (res.ok) { toast.success('Avatar updated'); return true; }
    toast.error(res.error || 'Could not save avatar');
    return false;
  };

  const saveBanner = async (bannerValue) => {
    const res = await updateProfile({ banner: bannerValue });
    if (res.ok) { toast.success('Banner updated ✨'); return true; }
    toast.error(res.error || 'Could not save banner');
    return false;
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl overflow-hidden bg-[#161622] border border-white/10">
        {/* Banner — accepts either a CSS gradient string or a `data:image/...`
            URL. Images render as backgroundImage; gradients render via the
            regular `background` shorthand. */}
        <div
          className="h-32 sm:h-40 md:h-48 relative"
          style={
            user.banner && user.banner.startsWith('data:image')
              ? { backgroundImage: `url(${user.banner})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { background: user.banner }
          }
          data-testid="profile-banner"
        >
          <div className="absolute inset-0 bg-black/25" />
          <button
            onClick={() => setBannerOpen(true)}
            data-testid="open-banner-picker"
            className="absolute top-3 right-3 p-2 rounded-full bg-black/40 backdrop-blur hover:bg-black/60 transition flex items-center gap-1.5"
            title="Change banner"
            aria-label="Change banner"
          >
            <Camera size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-white/90 pr-1 hidden sm:inline">Banner</span>
          </button>
        </div>

        {/* Identity row — avatar overlaps banner; medal + rank pill sit next to it */}
        <div className="px-5 pb-5">
          <div className="flex items-start gap-4 sm:gap-5 -mt-14 sm:-mt-16">
            <button
              onClick={() => setAvatarOpen(true)}
              data-testid="open-avatar-picker"
              className="relative group rounded-full shrink-0"
              title="Change avatar"
            >
              <Avatar className="w-28 h-28 sm:w-32 sm:h-32 ring-4 ring-[#0A0A0F]" style={{ boxShadow: `0 0 0 4px ${rank.color}` }}>
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="bg-fuchsia-500/40 text-xl">{user.displayName?.slice(0,2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <Camera size={18} className="text-white" />
              </div>
              {/* Medal pinned to the avatar's bottom-right, like a real award pin */}
              <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3">
                <RankMedal rank={user.rank} size={72} data-testid="profile-rank-medal" />
              </div>
            </button>

            {/* Right column — name, email, rank text (medal-forward, tight vertical rhythm) */}
            <div className="min-w-0 flex-1 pt-16 sm:pt-20">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-bold text-xl sm:text-2xl truncate">{user.displayName}</h1>
                <span className="text-lg">{user.flag}</span>
                <VipBadge active={user.is_vip} size="lg" animated data-testid="profile-vip-badge" />
                <FounderBadge active={user.is_founder} size="lg" data-testid="profile-founder-badge" />
              </div>
              <div className="text-white/50 text-xs truncate max-w-[260px] sm:max-w-none mt-0.5" title={user.email}>{user.email}</div>
            </div>
          </div>

          {/* Rank + activity tier + actions on a dedicated row below identity */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border"
                style={{ color: rank.color, borderColor: `${rank.color}55`, background: `${rank.color}12` }}
              >
                <Trophy size={12} /> {rank.name} · Rank {rank.rank}
              </span>
              {user.activity_name && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
                  style={{ color: user.activity_color, background: `${user.activity_color}1A`, border: `1px solid ${user.activity_color}33` }}
                  title="Activity tier — earned by being active. Unlocks cosmetics that can't be purchased."
                  data-testid="profile-activity-tier"
                >
                  <Sparkles size={9} /> {user.activity_name}
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full"><Edit2 size={12} className="mr-1" />Edit</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#161622] border-white/10 text-white">
                  <DialogHeader><DialogTitle className="font-display">Edit profile</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input value={edit.displayName} onChange={e => setEdit({ ...edit, displayName: e.target.value })} placeholder="Display name" className="bg-white/5 border-white/10 text-white" />
                    <Textarea value={edit.bio} onChange={e => setEdit({ ...edit, bio: e.target.value })} placeholder="Bio" className="bg-white/5 border-white/10 text-white min-h-[100px]" />
                  </div>
                  <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              {user.auth_method !== 'google' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPwOpen(true)}
                  data-testid="open-change-password"
                  className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full"
                >
                  <Lock size={12} className="mr-1" />Password
                </Button>
              )}
              <Button size="sm" onClick={logout} variant="outline" className="border-white/15 bg-white/5 hover:bg-rose-500/20 text-white rounded-full"><LogOut size={12} className="mr-1" />Logout</Button>
            </div>
          </div>

          <p className="mt-3 text-white/80 text-sm max-w-2xl">{user.bio}</p>

          {user.relationship && (
            <div className="mt-4">
              <RelationshipBadge
                relationship={user.relationship}
                isSelf
                onEnded={() => refresh?.()}
              />
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <Stat label="Followers" value={user.followers} icon={<Users size={12} className="text-cyan-400" />} />
            <Stat label="Following" value={user.following} icon={<Heart size={12} className="text-fuchsia-400" />} />
            <Stat label="Gifts" value={user.giftsReceived} icon={<Gift size={12} className="text-amber-400" />} />
            <Stat label="Planets" value={user.planetsJoined} icon={<Globe2 size={12} className="text-emerald-400" />} />
          </div>

          <div className="mt-5 rounded-2xl p-4 border" style={{ borderColor: rank.color + '44', background: rank.color + '10' }}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold" style={{ color: rank.color }}>{rank.name}</span>
              <span className="text-white/60" data-testid="profile-rank-xp">{(user.xp || 0).toLocaleString()} / {next ? next.xp.toLocaleString() : '∞'} XP</span>
              {next && <span style={{ color: next.color }} className="font-semibold">{next.name}</span>}
            </div>
            <div className="mt-2 h-2 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${rank.color}, ${next?.color || rank.color})` }} />
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-white/50">Earn XP by engaging — chat, gifts, games, daily tasks.</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    data-testid="profile-earn-xp-btn"
                    className="bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white rounded-full font-semibold border-0 hover:from-cyan-500 hover:to-fuchsia-600"
                  >
                    <Zap size={12} className="mr-1" fill="currentColor" /> Earn XP
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-72 bg-[#161622] border-white/10 text-white p-0"
                  data-testid="profile-earn-xp-popover"
                >
                  <div className="p-3 border-b border-white/10">
                    <div className="font-display font-semibold text-sm flex items-center gap-1.5">
                      <Zap size={13} className="text-cyan-300" fill="currentColor" /> Ways to earn XP
                    </div>
                    <div className="text-[11px] text-white/50 mt-0.5">XP grows your Cosmic Rank. Stars don&apos;t.</div>
                  </div>
                  <ul className="p-2 text-xs space-y-1">
                    <XpRow icon={<MessageSquare size={12} className="text-cyan-300" />} label="Send a chat message" amount="+5 XP" />
                    <XpRow icon={<Gift size={12} className="text-amber-300" />} label="Send a gift" amount="+10 XP" />
                    <XpRow icon={<Gamepad2 size={12} className="text-fuchsia-300" />} label="Play a mini-game" amount="+25 XP" />
                    <XpRow icon={<Sparkles size={12} className="text-violet-300" />} label="Daily Spin" amount="+25 XP" />
                    <XpRow icon={<CheckCircle2 size={12} className="text-emerald-300" />} label="Complete a daily task" amount="+50 XP" />
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="inventory">
        <TabsList className="bg-[#161622] border border-white/10">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-white/10">Gift Inventory</TabsTrigger>
          <TabsTrigger value="achievements" className="data-[state=active]:bg-white/10">Achievements</TabsTrigger>
          <TabsTrigger value="badges" className="data-[state=active]:bg-white/10">Badges</TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-white/10" data-testid="profile-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4 space-y-4">
          {/* Inventory summary */}
          <div className="grid grid-cols-3 gap-3">
            <SummaryCard label="Unique gifts" value={inventory.unique_types} icon={<Package size={14} className="text-cyan-400" />} />
            <SummaryCard label="Total received" value={inventory.total_count} icon={<Gift size={14} className="text-amber-400" />} />
            <SummaryCard label="Total value" value={`${(inventory.total_value || 0).toLocaleString()} ★`} icon={<Star size={14} className="text-amber-300" />} />
          </div>

          {/* Inventory grid */}
          {inventory.items.length === 0 ? (
            <Card className="bg-[#161622] border-white/10">
              <CardContent className="p-10 text-center">
                <Gift className="mx-auto text-fuchsia-400/60" size={36} />
                <div className="mt-3 font-medium">No gifts yet</div>
                <div className="text-xs text-white/50 mt-1">When other travelers send you gifts, they&apos;ll appear here.</div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {inventory.items.map(item => {
                const rarityColor = item.rarity === 'mythic' ? '#C084FC' : item.rarity === 'legendary' ? '#FBBF24' : item.rarity === 'epic' ? '#F472B6' : item.rarity === 'rare' ? '#00C6FF' : '#A8A8B3';
                return (
                  <div key={item.gift_id} className="relative rounded-2xl p-3 bg-[#161622] border text-center hover:-translate-y-0.5 transition" style={{ borderColor: rarityColor + '55' }}>
                    <div className="absolute -top-2 -right-2 min-w-6 h-6 px-2 rounded-full bg-fuchsia-500 text-white text-[11px] font-bold flex items-center justify-center shadow-lg">x{item.count}</div>
                    <div className="text-4xl">{item.emoji}</div>
                    <div className="text-[11px] font-semibold mt-2 truncate">{item.name}</div>
                    <div className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: rarityColor }}>{item.rarity}</div>
                    <div className="text-[10px] text-amber-300 mt-0.5 flex items-center justify-center gap-0.5"><Star size={8} fill="currentColor" />{(item.total_value || 0).toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent history */}
          {history.length > 0 && (
            <Card className="bg-[#161622] border-white/10">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Recent gifts received</div>
                <div className="space-y-1.5">
                  {history.slice(0, 8).map(h => (
                    <div key={h.id} className="flex items-center gap-3 text-sm p-2 rounded-lg bg-white/3">
                      <span className="text-2xl">{h.gift_emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate"><span className="font-semibold">{h.gift_name}</span> from <span className="text-cyan-300">{h.sender_name}</span></div>
                        <div className="text-[10px] text-white/40">{new Date(h.created_at).toLocaleString()}</div>
                      </div>
                      <span className="text-amber-300 text-xs flex items-center gap-0.5"><Star size={9} fill="currentColor" />{h.price}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="achievements" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { name: 'First Steps', desc: 'Sent your first message', emoji: '\ud83d\udc63', earned: true },
              { name: 'Globe Trotter', desc: 'Joined 10+ planets', emoji: '\ud83c\udf0d', earned: (user.planetsJoined || 0) >= 10 },
              { name: 'Heart of Gold', desc: 'Sent 50+ gifts', emoji: '\ud83d\udc96', earned: false },
              { name: 'Cosmonaut', desc: 'Reached Rank 5', emoji: '\u2728', earned: rank.rank >= 5 },
              { name: 'Star Commander', desc: 'Reach Rank 6', emoji: '\u2b50', earned: rank.rank >= 6 },
              { name: 'Lucky Streak', desc: 'Win 5 games in a row', emoji: '\ud83c\udfb0', earned: false },
            ].map((a, i) => (
              <div key={i} className={`p-4 rounded-xl border ${a.earned ? 'bg-[#161622] border-amber-400/30' : 'bg-white/3 border-white/5 opacity-50'}`}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{a.emoji}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm flex items-center gap-2">{a.name}{a.earned && <Sparkles size={11} className="text-amber-300" />}</div>
                    <div className="text-xs text-white/50 mt-1">{a.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="badges" className="mt-4 space-y-4">
          {/* Rank medal collection — highlights the tier the user is on */}
          <Card className="bg-[#161622] border-white/10">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="text-amber-300" size={16} />
                <div className="font-display font-semibold">Rank medals</div>
                <span className="text-xs text-white/40">— unlock each tier by climbing ranks</span>
              </div>
              <MedalCollection currentRank={user.rank} />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
            <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 bg-amber-400/15 border border-amber-400/30">
                <Crown className="text-amber-300" size={26} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold">Unlock VIP Badges</div>
                <div className="text-xs text-white/60 mt-0.5">Premium members get an animated profile aura and exclusive badges.</div>
              </div>
              <Button
                onClick={() => setBuyOpen(true)}
                data-testid="profile-vip-upgrade-btn"
                className="sm:self-center bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:from-amber-500 hover:to-orange-600 rounded-full font-semibold border-0"
              >
                Top up Stars
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4 space-y-4">
          <SettingsCard
            user={user}
            updateProfile={updateProfile}
            onOpenChangePassword={() => setPwOpen(true)}
            logout={logout}
          />
        </TabsContent>
      </Tabs>

      <AvatarPicker
        open={avatarOpen}
        onOpenChange={setAvatarOpen}
        currentAvatar={user.avatar}
        onSave={saveAvatar}
      />
      <BannerPicker
        open={bannerOpen}
        onOpenChange={setBannerOpen}
        currentBanner={user.banner}
        onSave={saveBanner}
      />
      <BuyStars open={buyOpen} onOpenChange={setBuyOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ cur: false, next: false, conf: false });
  const [loading, setLoading] = useState(false);

  // Reset fields each time the dialog closes
  useEffect(() => {
    if (!open) { setCurrent(''); setNext(''); setConfirm(''); setShow({ cur: false, next: false, conf: false }); }
  }, [open]);

  // Cheap strength heuristic — used for the inline progress bar
  const strength = (() => {
    if (!next) return { score: 0, label: '', color: '#444' };
    let s = 0;
    if (next.length >= 8) s++;
    if (next.length >= 12) s++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s++;
    if (/\d/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    const labels = ['Too short', 'Weak', 'Okay', 'Good', 'Strong', 'Excellent'];
    const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#06b6d4'];
    return { score: s, label: labels[s], color: colors[s] };
  })();

  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !loading;
  const mismatch = confirm.length > 0 && next !== confirm;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password changed successfully');
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not change password');
    } finally {
      setLoading(false);
    }
  };

  // Single password-field renderer — kept inline (not a sub-component) so React
  // doesn't tear down the input on each parent re-render and lose focus.
  const renderField = ({ value, onChange, placeholder, visKey, testId, autoComplete }) => (
    <div className="relative">
      <Input
        type={show[visKey] ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-white/5 border-white/10 text-white pr-10"
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow(s => ({ ...s, [visKey]: !s[visKey] }))}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
        aria-label={show[visKey] ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {show[visKey] ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Lock size={16} className="text-fuchsia-400" /> Change password
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Update the password used to sign in to your WorldVibe account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="change-password-form">
          {renderField({ value: current, onChange: setCurrent, placeholder: 'Current password', visKey: 'cur', testId: 'cp-current', autoComplete: 'current-password' })}
          {renderField({ value: next, onChange: setNext, placeholder: 'New password (min 8 characters)', visKey: 'next', testId: 'cp-new', autoComplete: 'new-password' })}
          {next && (
            <div data-testid="cp-strength">
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
              </div>
              <div className="text-[11px] mt-1" style={{ color: strength.color }}>{strength.label}</div>
            </div>
          )}
          {renderField({ value: confirm, onChange: setConfirm, placeholder: 'Confirm new password', visKey: 'conf', testId: 'cp-confirm', autoComplete: 'new-password' })}
          {mismatch && (
            <div className="text-[11px] text-rose-400" data-testid="cp-mismatch">Passwords don&apos;t match</div>
          )}
          <p className="text-[11px] text-white/40">
            For your security, you&apos;ll stay signed in here, but other devices may need to sign in again.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="cp-submit"
            className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="rounded-xl p-3 bg-white/3 border border-white/5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/50">{icon}{label}</div>
      <div className="font-display font-bold text-lg mt-0.5">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="rounded-xl p-4 bg-[#161622] border border-white/10">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/50">{icon}{label}</div>
      <div className="font-display font-bold text-xl mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

function XpRow({ icon, label, amount }) {
  return (
    <li className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-white/5">
      <span className="flex items-center gap-1.5 text-white/80">{icon}{label}</span>
      <span className="text-cyan-300 font-semibold tabular-nums">{amount}</span>
    </li>
  );
}

// ============================================================================
// MedalCollection — grid of all 12 rank medals. The user's current rank is
// highlighted with a Current pill and a colored glow. Future ranks appear
// grayscale + locked.
// ============================================================================
function MedalCollection({ currentRank }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {ALL_RANK_MEDALS.map((m) => {
        const unlocked = currentRank >= m.rank;
        const isCurrent = m.rank === currentRank;
        return (
          <div
            key={m.rank}
            data-testid={`medal-rank-${m.rank}`}
            className={`relative rounded-2xl p-4 flex flex-col items-center gap-3 border transition ${
              unlocked ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-black/25 opacity-80'
            }`}
            style={{
              borderColor: isCurrent ? m.color : (unlocked ? m.color + '55' : 'rgba(255,255,255,0.06)'),
              boxShadow: isCurrent ? `0 0 0 2px ${m.color}, 0 0 32px ${m.color}66` : (unlocked ? `0 0 20px ${m.color}22` : 'none'),
            }}
          >
            {isCurrent && (
              <span
                className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ background: m.color, color: '#0A0A0F' }}
              >
                Current
              </span>
            )}
            {/* Medal artwork — full square with radial glow behind for lift */}
            <div
              className="relative w-full aspect-square rounded-xl overflow-hidden flex items-center justify-center"
              style={{
                background: unlocked
                  ? `radial-gradient(circle at 50% 50%, ${m.accent}22 0%, transparent 65%), #0a0a15`
                  : '#0a0a15',
              }}
            >
              <img
                src={m.src}
                alt={m.name}
                loading="lazy"
                className="w-[86%] h-[86%] object-contain"
                style={{
                  filter: unlocked
                    ? `drop-shadow(0 0 14px ${m.accent}99)`
                    : 'grayscale(1) brightness(0.5)',
                }}
              />
              {!unlocked && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Lock size={26} className="text-white/80" />
                </div>
              )}
            </div>
            <div className="text-center">
              <div
                className="text-sm font-display font-bold leading-tight"
                style={{ color: unlocked ? m.color : 'rgba(255,255,255,0.55)' }}
              >
                {m.name}
              </div>
              <div className="text-[11px] text-white/50 mt-0.5">Rank {m.rank}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}



// Settings card — grouped toggles and account links on the Profile page.
// Kept alongside Profile.jsx (rather than in a dedicated file) because every
// row shares the same auth-context data — splitting adds no clarity here.
function SettingsCard({ user, updateProfile, onOpenChangePassword, logout }) {
  const [savingKey, setSavingKey] = React.useState(null);
  const [notif, setNotif] = React.useState(user.notifications_enabled !== false);
  const [dating, setDating] = React.useState(!!user.dating_enabled);
  const [wall, setWall] = React.useState(!!user.show_on_founder_wall);
  const [restoring, setRestoring] = React.useState(false);
  const native = isNativeMobile();

  // Sync local toggle state when the auth-context user object refreshes
  // (e.g. after login on another tab).
  React.useEffect(() => {
    setNotif(user.notifications_enabled !== false);
    setDating(!!user.dating_enabled);
    setWall(!!user.show_on_founder_wall);
  }, [user.notifications_enabled, user.dating_enabled, user.show_on_founder_wall]);

  const persist = async (key, value, applyOptimistic) => {
    setSavingKey(key);
    applyOptimistic(value);
    try {
      await updateProfile({ [key]: value });
    } catch (e) {
      applyOptimistic(!value); // revert
      toast.error(e?.response?.data?.detail || 'Could not save setting');
    } finally {
      setSavingKey(null);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restorePurchases();
      toast.success('Restore complete — any prior purchases have been re-applied.');
    } catch (e) {
      toast.error(e?.message || 'Could not restore purchases');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Card className="bg-[#161622] border-white/10" data-testid="profile-settings-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <SettingsIcon className="text-cyan-300" size={16} />
          <div className="font-display font-semibold">Settings</div>
          <span className="text-xs text-white/40">— preferences, security, account</span>
        </div>

        <div className="space-y-1">
          {/* Notifications toggle */}
          <SettingsRow
            icon={<Bell size={15} className="text-amber-300" />}
            label="Push notifications"
            desc="Chat mentions, gifts, follows, and daily rewards."
            testId="settings-notifications"
            right={
              <Switch
                checked={notif}
                onCheckedChange={(v) => persist('notifications_enabled', v, setNotif)}
                disabled={savingKey === 'notifications_enabled'}
                data-testid="settings-notifications-switch"
              />
            }
          />
          {/* Dating profile toggle */}
          <SettingsRow
            icon={<Heart size={15} className="text-rose-300" />}
            label="Dating profile visible"
            desc="Turn off to hide your profile from Dating swipes and Find A Match."
            testId="settings-dating"
            right={
              <Switch
                checked={dating}
                onCheckedChange={(v) => persist('dating_enabled', v, setDating)}
                disabled={savingKey === 'dating_enabled'}
                data-testid="settings-dating-switch"
              />
            }
          />
          {/* Founder Wall public visibility toggle — off by default. Users
              must explicitly opt in to have their name/avatar/country/rank
              shown on the PUBLIC landing page. Wording is intentionally
              blunt so nobody appears without informed consent. */}
          <SettingsRow
            icon={<Trophy size={15} className="text-amber-300" />}
            label="Show me on the public Founder Wall"
            desc="Displays your display name, avatar, country flag, and cosmic rank on the public landing page — visible to anyone, no sign-in needed."
            testId="settings-founder-wall"
            right={
              <Switch
                checked={wall}
                onCheckedChange={(v) => persist('show_on_founder_wall', v, setWall)}
                disabled={savingKey === 'show_on_founder_wall'}
                data-testid="settings-founder-wall-switch"
              />
            }
          />
          {/* Change password (only for non-Google accounts) */}
          {user.auth_method !== 'google' && (
            <SettingsRow
              onClick={onOpenChangePassword}
              icon={<Lock size={15} className="text-fuchsia-300" />}
              label="Change password"
              desc="Rotate the password used to sign in to your WorldVibe account."
              testId="settings-change-password"
              right={<ChevronRight size={15} className="text-white/40" />}
            />
          )}
          {/* Restore Purchases — native shells only (Apple guideline 3.1.1) */}
          {native && (
            <SettingsRow
              onClick={handleRestore}
              icon={restoring
                ? <Loader2 size={15} className="text-sky-300 animate-spin" />
                : <RotateCcw size={15} className="text-sky-300" />}
              label={restoring ? 'Restoring purchases…' : 'Restore purchases'}
              desc="Re-apply any prior VIP or Star pack purchases from your store account."
              disabled={restoring}
              testId="settings-restore-purchases"
              right={<ChevronRight size={15} className="text-white/40" />}
            />
          )}
          {/* Help & Support */}
          <SettingsRow
            as={Link}
            to="/app/help"
            icon={<HelpCircle size={15} className="text-emerald-300" />}
            label="Help & Support"
            desc="FAQs, contact us, and community guidelines."
            testId="settings-help"
            right={<ChevronRight size={15} className="text-white/40" />}
          />
          {/* Privacy / trust */}
          <SettingsRow
            icon={<ShieldCheck size={15} className="text-cyan-300" />}
            label="Privacy & data"
            desc="Your data is never sold. See our privacy policy."
            testId="settings-privacy"
            as="a"
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            right={<ChevronRight size={15} className="text-white/40" />}
          />
          {/* Sign out */}
          <SettingsRow
            onClick={logout}
            icon={<LogOut size={15} className="text-rose-300" />}
            label="Sign out"
            desc="End this session on this device."
            testId="settings-signout"
            right={<ChevronRight size={15} className="text-white/40" />}
          />
          {/* Danger zone — permanent account deletion (Play Data Deletion policy) */}
          <DeleteAccountRow user={user} logout={logout} />
        </div>
      </CardContent>
    </Card>
  );
}

// Single settings row. Renders as a Link, an anchor, or a button depending on
// which of `as` / `to` / `href` / `onClick` are provided.
function SettingsRow({ icon, label, desc, right, onClick, as, to, href, target, rel, disabled, testId, ...rest }) {
  const inner = (
    <>
      <span className="w-8 h-8 shrink-0 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center">{icon}</span>
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-sm font-medium">{label}</span>
        {desc && <span className="block text-[11px] text-white/45 mt-0.5">{desc}</span>}
      </span>
      {right}
    </>
  );
  const cls = "flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl hover:bg-white/5 transition disabled:opacity-60 disabled:cursor-not-allowed w-full";
  if (as === Link && to) return <Link to={to} className={cls} data-testid={testId} {...rest}>{inner}</Link>;
  if (as === 'a' && href)  return <a href={href} target={target} rel={rel} className={cls} data-testid={testId} {...rest}>{inner}</a>;
  if (onClick) return <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testId} {...rest}>{inner}</button>;
  return <div className={cls} data-testid={testId} {...rest}>{inner}</div>;
}

// Danger-zone row + confirm dialog for account deletion. Deletion is
// irreversible and clears our auth cookies server-side. For password
// accounts we ask for the password. For Google-auth users (no password
// on file) we ask them to type "DELETE MY ACCOUNT" verbatim so a stray
// tap can't nuke the account.
function DeleteAccountRow({ user, logout }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const hasPassword = user?.auth_method !== 'google' && user?.auth_method !== 'apple';
  const PHRASE = 'DELETE MY ACCOUNT';

  const canSubmit = hasPassword ? password.length > 0 : phrase.trim().toUpperCase() === PHRASE;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      await api.post('/auth/delete-account', {
        password: hasPassword ? password : undefined,
        confirm_phrase: hasPassword ? undefined : phrase.trim(),
        reason: reason.trim() || undefined,
      });
      toast.success('Your account has been permanently deleted.');
      setOpen(false);
      // Small delay so the toast can render before the logout redirect
      setTimeout(() => { try { logout?.(); } catch (_) { /* logout side-effects are non-fatal */ } window.location.href = '/'; }, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="settings-delete-account"
        className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-xl hover:bg-red-500/10 transition w-full"
      >
        <span className="w-8 h-8 shrink-0 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center">
          <Trash2 size={15} className="text-red-300" />
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-sm font-medium text-red-200">Delete account</span>
          <span className="block text-[11px] text-white/45 mt-0.5">
            Permanently remove your account and personal data. This cannot be undone.
          </span>
        </span>
        <ChevronRight size={15} className="text-white/40" />
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="bg-[#161622] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-200">
              <AlertTriangle size={18} /> Delete your WorldVibe account?
            </DialogTitle>
            <DialogDescription className="text-white/70 leading-relaxed">
              This is <b className="text-red-200">permanent</b>. We&apos;ll immediately delete your profile,
              feed posts, quest streak, DMs you sent (replaced with &quot;message removed&quot; for the other
              side), pets, follows, and settings. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {hasPassword ? (
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/50">Confirm your password</Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="••••••••"
                  data-testid="delete-account-password"
                  disabled={busy}
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/50">
                  Type <span className="text-red-200 font-semibold">{PHRASE}</span> to confirm
                </Label>
                <Input
                  value={phrase}
                  onChange={e => setPhrase(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder={PHRASE}
                  data-testid="delete-account-phrase"
                  disabled={busy}
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">
                Reason (optional — helps us improve)
              </Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="bg-white/5 border-white/10 text-white min-h-[70px] mt-1"
                placeholder="Anything you'd like us to know before you go…"
                maxLength={500}
                data-testid="delete-account-reason"
                disabled={busy}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="delete-account-cancel"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit || busy}
              data-testid="delete-account-confirm"
              className="bg-red-500 hover:bg-red-600 text-white border-0"
            >
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Trash2 size={14} className="mr-1" />}
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
